import { Action } from './actions/action';
import { Board } from './board';
import { Card } from './card/card';
import { PossibleMovesFinder, PossibleMove } from './possible-moves-finder';
import { CardIndexMapping } from './card/card-index-mapping';
import { AutoFinishMoveCommand } from './commands/auto-finish-move-command';
import { Command } from './commands/command';
import { CommandListSerializer } from './commands/command-list-serializer';
import { DrawDeckMoveCommand } from './commands/draw-deck-move-command';
import { MoveCommand } from './commands/move-command';
import { BackToDeckMoveCommand } from './commands/back-to-deck-move-command';
import { UndoCommand } from './commands/undo-command';
import { GameLogicScore, SolitaireScoreData } from './game-logic-score';
import { CardDisclosureStatus, CardValue, PileType } from './logic-types';
import { GameLogicDepth } from './game-logic-depth';
import { Dealer, GameLogic } from 'game-logic';

export type OnBoardChangedAction = () => void;
export type OnCommandCommittedAction = (moves: string[]) => void;
export type OnScoreChangedAction = (score: number) => void;

export class SolitaireGameLogic implements GameLogic {
  public get currentDealer(): Dealer<string> {
    return this.dealer;
  }

  public get shouldDealNextCard(): boolean {
    return this.dealtCards < this.cardsToDeal;
  }

  public get allCardsDisclosed(): boolean {
    return this.cardIndexMapping.allCardsDisclosed;
  }

  public get currentScore(): number {
    return this.gameLogicScore.currentScore;
  }

  public get isGameWon(): boolean {
    const allCards: Card[] = [];

    for (const pile of this.board.tableaus) {
      pile.combine(allCards);
    }

    return allCards.every((card) => card.isFaceUp);
  }

  public set onBoardChangedAction(value: OnBoardChangedAction) {
    this.boardChangedAction = value;
  }

  public set onCommandCommittedAction(value: OnCommandCommittedAction) {
    this.commandCommittedAction = value;
  }

  public set onScoreChangedAction(value: OnScoreChangedAction) {
    this.gameLogicScore.onScoreChangedAction = value;
  }

  private gameLogicScore: GameLogicScore;

  private runningDealerCardPromises: Promise<void>[] = [];
  private actionList: Action[] = [];
  private commandList: Command[] = [];
  private dealtCards = 0;
  private boardChangedAction?: OnBoardChangedAction;
  private commandCommittedAction?: OnCommandCommittedAction;

  private readonly cardsToDeal: number = 1 + 2 + 3 + 4 + 5 + 6 + 7;
  private readonly totalCardsNumber: number = 52;

  constructor(
    private dealer: Dealer<string>,
    public readonly board: Board,
    private cardIndexMapping: CardIndexMapping,
    public readonly gameLogicDepth: GameLogicDepth,
    private logicVersion: number,
  ) {
    this.gameLogicScore = new GameLogicScore();
  }

  public serializeCommandList(): string[] {
    return this.commandList.map((command) => command.serializeToString());
  }

  public generateScoreData(): SolitaireScoreData {
    return this.gameLogicScore.generateScoreData(this.board);
  }

  public discloseStartingCards(): void {
    this.markTopCardsFromTableauForDisclosure();
    this.markDeckCardsForDisclosure();
    this.disclosePendingCards();
  }

  public dealNextCard(): void {
    let cardToDeal = 0;

    for (let tableauIndex = 0; tableauIndex < this.board.tableaus.length; ++tableauIndex) {
      for (let cardIndex = 0; cardIndex < tableauIndex + 1; ++cardIndex) {
        if (this.dealtCards != cardToDeal) {
          cardToDeal++;
          continue;
        }

        const deck = this.board.deck;
        const card = deck.lastCard;
        if (card === undefined) {
          continue;
        }

        deck.removeCard(card);

        this.gameLogicDepth.updateCardDepth(card.index);

        this.board.tableaus[tableauIndex].addCard(card);

        if (cardIndex === tableauIndex) {
          card.openCard();
        }

        this.dealtCards++;

        this.boardChangedAction?.();
        return;
      }
    }
  }

  public newDeal(): void {
    this.discloseStartingCards();

    while (this.shouldDealNextCard) {
      this.dealNextCard();
    }
  }

  public async waitForAllTasksToBeCompleted(): Promise<void> {
    await Promise.all(this.runningDealerCardPromises);
    this.runningDealerCardPromises.length = 0;
  }

  public drawFromDeck(): void {
    if (this.shouldDealNextCard) {
      return;
    }

    if (this.board.deck.hasCards) {
      this.execute(new DrawDeckMoveCommand());
    } else if (this.board.waste.hasCards) {
      this.execute(new BackToDeckMoveCommand());
    }
  }

  public getHintedMoves(): PossibleMove[] {
    const allPossibleMoves = PossibleMovesFinder.findPossibleMoves(this.board);

    if (allPossibleMoves.tableauMoves.length > 0) {
      return allPossibleMoves.tableauMoves;
    }

    if (allPossibleMoves.topWasteMoves.length > 0) {
      return allPossibleMoves.topWasteMoves;
    }

    if (allPossibleMoves.deckMoves.length > 0) {
      // Return the first element as a list
      return allPossibleMoves.deckMoves.slice(0, 1);
    }

    if (allPossibleMoves.buriedWasteMoves.length > 0) {
      return allPossibleMoves.buriedWasteMoves;
    }

    return [];
  }

  public possibleMovesForCard(card: Card): MoveCommand[] {
    if (card.cardId === undefined) {
      return [];
    }

    const possibleMoves: MoveCommand[] = [];

    const originPile = this.board.getPileByCardIndex(card.index);

    if (originPile.type !== PileType.Tableau) {
      const lastCard = originPile.lastCard;

      // If the card is in the foundation, waste or deck, if must be at the top of the pile
      if (lastCard === undefined || lastCard.index !== card.index) {
        return possibleMoves;
      }
    }

    for (const pile of this.board.foundations) {
      if (pile.hasCards) {
        if (card.canMove(originPile, pile) && card.cardId.cardValue !== CardValue.Ace) {
          possibleMoves.push(new MoveCommand(card.cardId.id, pile.id));
          break;
        }
      } else {
        if (
          card.cardId.cardValue === CardValue.Ace &&
          this.board.getPileByCardIndex(card.index).type !== PileType.Foundation
        ) {
          possibleMoves.push(new MoveCommand(card.cardId.id, pile.id));
          break;
        }
      }
    }

    for (const pile of this.board.tableaus) {
      if (pile.hasCards) {
        if (
          card.canMove(originPile, pile) &&
          card.cardId.cardValue !== CardValue.King &&
          card.cardId.cardValue !== CardValue.Ace
        ) {
          possibleMoves.push(new MoveCommand(card.cardId.id, pile.id));
          break;
        }
      } else {
        if (card.cardId.cardValue === CardValue.King) {
          possibleMoves.push(new MoveCommand(card.cardId.id, pile.id));
          break;
        }
      }
    }

    return possibleMoves;
  }

  public autoMoveCard(card: Card): boolean {
    const possibleMoves = this.possibleMovesForCard(card);
    if (possibleMoves.length > 0) {
      this.execute(possibleMoves[0]);
      return true;
    }
    // There were no matches
    return false;
  }

  public autoFinishNextCard(): boolean {
    const allCards = this.board.getAllCards();

    for (const targetPile of this.board.foundations) {
      if (targetPile.cardCount >= 13) {
        continue;
      }

      for (const card of allCards) {
        if (
          card?.canMove(this.board.getPileByCardIndex(card.index), targetPile) &&
          card.cardId !== undefined
        ) {
          this.execute(new AutoFinishMoveCommand(card.cardId.id, targetPile.id));
          return true;
        }
      }
    }

    return false;
  }

  public execute(command: Command): void {
    let commandCommitted = false;

    /*
    if (!environment.production) {
      console.log(`🚀 Execute command: ${command.serializeToString()}`);
    }
    */

    if (MoveCommand.isCommand(command)) {
      commandCommitted = command.execute(
        this.board,
        this.gameLogicScore,
        this.gameLogicDepth,
        this.actionList,
      );
    } else if (AutoFinishMoveCommand.isCommand(command)) {
      commandCommitted = command.execute(
        this.board,
        this.gameLogicScore,
        this.gameLogicDepth,
        this.actionList,
      );
    } else if (DrawDeckMoveCommand.isCommand(command)) {
      commandCommitted = command.execute(
        this.board,
        this.gameLogicScore,
        this.gameLogicDepth,
        this.actionList,
      );
    } else if (BackToDeckMoveCommand.isCommand(command)) {
      commandCommitted = command.execute(
        this.board,
        this.gameLogicScore,
        this.gameLogicDepth,
        this.actionList,
      );
    } else if (UndoCommand.isCommand(command)) {
      if (!this.isGameWon) {
        commandCommitted = command.execute(this.actionList);
      }
    } else {
      throw new Error('Unknown command type');
    }

    /*
    if (!environment.production) {
      this.board.printLog(this.gameLogicDepth);
    }
    */

    if (!commandCommitted) {
      return;
    }

    this.commandList.push(command);

    this.commandCommittedAction?.([command.serializeToString()]);

    this.boardChangedAction?.();

    if (this.isGameWon) {
      this.markAllCardsForDisclosure();
    }

    this.disclosePendingCards();
  }

  private markTopCardsFromTableauForDisclosure(): void {
    let cardIndex = 51;

    for (let tableauIndex = 0; tableauIndex < this.board.tableaus.length; ++tableauIndex) {
      for (let cardInPileIndex = 0; cardInPileIndex < tableauIndex + 1; ++cardInPileIndex) {
        if (tableauIndex === cardInPileIndex) {
          this.cardIndexMapping.setStatusWhenNoneForIndex(cardIndex, CardDisclosureStatus.Pending);
        }

        cardIndex--;
      }
    }
  }

  private markAllCardsForDisclosure() {
    const allCards = this.board.getAllCards();
    for (let index = 0; index < allCards.length; ++index) {
      this.cardIndexMapping.setStatusWhenNoneForIndex(index, CardDisclosureStatus.Pending);
    }
  }

  private markDeckCardsForDisclosure(): void {
    for (let cardIndex = 0; cardIndex < this.totalCardsNumber - this.cardsToDeal; ++cardIndex) {
      this.cardIndexMapping.setStatusWhenNoneForIndex(cardIndex, CardDisclosureStatus.Pending);
    }
  }

  private disclosePendingCards(): void {
    const allCards = this.board.getAllCards();

    const cardIndexes = allCards
      .filter((card) =>
        this.cardIndexMapping.setStatusWhenPendingForIndex(
          card.index,
          CardDisclosureStatus.InProgress,
        ),
      )
      .map((card) => card.index);

    if (cardIndexes.length === 0) {
      return;
    }

    // if (!environment.production) {
    //   console.log(`🤔 askDealerForCardIds: [${cardIndexes.join(', ')}]`);
    // }

    const result = this.askDealerForCardIds(
      cardIndexes,
      CommandListSerializer.getHash(this.commandList),
    );

    this.runningDealerCardPromises.push(result);
  }

  private async askDealerForCardIds(indexes: number[], hash: string): Promise<void> {
    await this.dealer.discloseSecrets(indexes, hash).then((ids) => {
      // if (!environment.production) {
      //   console.log(`🤝 dealDeck: [${ids.join(', ')}]`);
      // }
      this.cardIndexMapping.dealDeck(indexes, ids);
      /*
      if (!environment.production) {
        this.board.printLog(this.gameLogicDepth);
      }
      */
    });
  }
}
